import React, { useState, useEffect } from 'react';
import { 
  Star, MessageSquare, Eye, EyeOff, Trash2, Loader2, 
  RefreshCw, Plus, Edit, CheckCircle, XCircle, Filter,
  Award, ThumbsUp, ThumbsDown, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription
} from '@/components/ui/alert';
import { toast } from 'sonner';
import api from '@/services/api';

// Star rating component
const StarRating = ({ rating, size = 'default', interactive = false, onChange }) => {
  const sizeClass = size === 'small' ? 'h-3 w-3' : size === 'large' ? 'h-6 w-6' : 'h-4 w-4';
  
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${
            star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
          } ${interactive ? 'cursor-pointer hover:text-yellow-400' : ''}`}
          onClick={() => interactive && onChange && onChange(star)}
        />
      ))}
    </div>
  );
};

const ReviewsPanel = () => {
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({
    reviews_enabled: true,
    auto_approve_5_star: false,
    min_rating_to_show: 4
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [filterRating, setFilterRating] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    rating: 5,
    text: '',
    service: 'delivery',
    verified: true,
    approved: true,
    featured: false
  });

  useEffect(() => {
    fetchReviews();
    fetchSettings();
  }, []);

  const fetchReviews = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/admin/reviews');
      if (response.data.success) {
        setReviews(response.data.reviews);
      }
    } catch (error) {

      // Use local testimonials as fallback
      setReviews([]);
      toast.error('Failed to load reviews from server');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/admin/settings');
      if (response.data.success && response.data.settings) {
        setSettings({
          reviews_enabled: response.data.settings.reviews_enabled !== false,
          auto_approve_5_star: response.data.settings.auto_approve_5_star || false,
          min_rating_to_show: response.data.settings.min_rating_to_show || 4
        });
      }
    } catch (error) {

    }
  };

  const handleToggleApproval = async (reviewId, currentStatus) => {
    try {
      await api.put(`/api/admin/reviews/${reviewId}`, { approved: !currentStatus });
      setReviews(prev => prev.map(r => 
        r.review_id === reviewId ? { ...r, approved: !currentStatus } : r
      ));
      toast.success(currentStatus ? 'Review hidden' : 'Review approved');
    } catch (error) {

      toast.error('Failed to update review');
    }
  };

  const handleToggleFeatured = async (reviewId, currentStatus) => {
    try {
      await api.put(`/api/admin/reviews/${reviewId}`, { featured: !currentStatus });
      setReviews(prev => prev.map(r => 
        r.review_id === reviewId ? { ...r, featured: !currentStatus } : r
      ));
      toast.success(currentStatus ? 'Removed from featured' : 'Added to featured');
    } catch (error) {

      toast.error('Failed to update review');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!confirm('Are you sure you want to delete this review?')) return;
    
    try {
      await api.delete(`/api/admin/reviews/${reviewId}`);
      setReviews(prev => prev.filter(r => r.review_id !== reviewId));
      toast.success('Review deleted');
    } catch (error) {

      toast.error('Failed to delete review');
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.text) {
      toast.error('Name and review text are required');
      return;
    }

    setIsSaving(true);
    try {
      if (editingReview) {
        await api.put(`/api/admin/reviews/${editingReview.review_id}`, formData);
        toast.success('Review updated');
      } else {
        await api.post('/api/admin/reviews', formData);
        toast.success('Review created');
      }
      setShowCreateModal(false);
      setEditingReview(null);
      resetForm();
      fetchReviews();
    } catch (error) {

      toast.error('Failed to save review');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await api.put('/api/admin/settings', { 
        settings: {
          reviews_enabled: settings.reviews_enabled,
          auto_approve_5_star: settings.auto_approve_5_star,
          min_rating_to_show: settings.min_rating_to_show
        }
      });
      toast.success('Review settings saved');
    } catch (error) {

      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location: '',
      rating: 5,
      text: '',
      service: 'delivery',
      verified: true,
      approved: true,
      featured: false
    });
  };

  const openEditModal = (review) => {
    setEditingReview(review);
    setFormData({
      name: review.name,
      location: review.location || '',
      rating: review.rating,
      text: review.text,
      service: review.service || 'delivery',
      verified: review.verified !== false,
      approved: review.approved !== false,
      featured: review.featured || false
    });
    setShowCreateModal(true);
  };

  const filteredReviews = reviews.filter(review => {
    if (filterRating !== 'all' && review.rating !== parseInt(filterRating)) return false;
    if (filterStatus === 'approved' && !review.approved) return false;
    if (filterStatus === 'hidden' && review.approved !== false) return false;
    if (filterStatus === 'featured' && !review.featured) return false;
    return true;
  });

  const stats = {
    total: reviews.length,
    approved: reviews.filter(r => r.approved !== false).length,
    hidden: reviews.filter(r => r.approved === false).length,
    featured: reviews.filter(r => r.featured).length,
    avgRating: reviews.length > 0 
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : '0.0'
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#039fff]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reviews-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#001A47]">Reviews & Testimonials</h2>
          <p className="text-gray-500">Manage customer reviews displayed on the website</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchReviews}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => { resetForm(); setEditingReview(null); setShowCreateModal(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Review
          </Button>
        </div>
      </div>

      {/* Global Toggle */}
      <Alert className={settings.reviews_enabled ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {settings.reviews_enabled ? (
              <Eye className="h-5 w-5 text-green-600" />
            ) : (
              <EyeOff className="h-5 w-5 text-red-600" />
            )}
            <div>
              <p className={`font-medium ${settings.reviews_enabled ? 'text-green-800' : 'text-red-800'}`}>
                Reviews Section: {settings.reviews_enabled ? 'VISIBLE' : 'HIDDEN'}
              </p>
              <AlertDescription className={settings.reviews_enabled ? 'text-green-700' : 'text-red-700'}>
                {settings.reviews_enabled 
                  ? 'Approved reviews are displayed on the website'
                  : 'All reviews are hidden from the website'
                }
              </AlertDescription>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Switch 
              checked={settings.reviews_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, reviews_enabled: checked }))}
            />
            <Button size="sm" onClick={handleSaveSettings} disabled={isSaving}>
              Save
            </Button>
          </div>
        </div>
      </Alert>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#001A47]">{stats.total}</p>
              <p className="text-sm text-gray-500">Total Reviews</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{stats.approved}</p>
              <p className="text-sm text-gray-500">Approved</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">{stats.hidden}</p>
              <p className="text-sm text-gray-500">Hidden</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">{stats.featured}</p>
              <p className="text-sm text-gray-500">Featured</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                <p className="text-3xl font-bold text-[#fb6402]">{stats.avgRating}</p>
              </div>
              <p className="text-sm text-gray-500">Avg Rating</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="text-sm text-gray-500">Filter:</span>
        </div>
        <Select value={filterRating} onValueChange={setFilterRating}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Rating" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Ratings</SelectItem>
            <SelectItem value="5">5 Stars</SelectItem>
            <SelectItem value="4">4 Stars</SelectItem>
            <SelectItem value="3">3 Stars</SelectItem>
            <SelectItem value="2">2 Stars</SelectItem>
            <SelectItem value="1">1 Star</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="hidden">Hidden</SelectItem>
            <SelectItem value="featured">Featured</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {filteredReviews.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600">No reviews found</h3>
              <p className="text-gray-500">Add your first review or adjust filters</p>
            </CardContent>
          </Card>
        ) : (
          filteredReviews.map((review) => (
            <Card 
              key={review.review_id} 
              className={`${review.approved === false ? 'opacity-60 border-red-200' : ''} ${review.featured ? 'border-purple-300 bg-purple-50/30' : ''}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <StarRating rating={review.rating} />
                      <Badge variant={review.approved !== false ? 'default' : 'destructive'} className={review.approved !== false ? 'bg-green-100 text-green-800' : ''}>
                        {review.approved !== false ? 'Approved' : 'Hidden'}
                      </Badge>
                      {review.featured && (
                        <Badge className="bg-purple-100 text-purple-800">
                          <Award className="h-3 w-3 mr-1" />
                          Featured
                        </Badge>
                      )}
                      {review.verified && (
                        <Badge variant="outline" className="text-green-600 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-gray-700 mb-3">"{review.text}"</p>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="font-medium text-gray-700">{review.name}</span>
                      {review.location && <span>{review.location}</span>}
                      <Badge variant="secondary" className="capitalize">
                        {review.service === 'mini-move' ? 'Mini-Move' : review.service}
                      </Badge>
                      {review.date && <span>{new Date(review.date).toLocaleDateString()}</span>}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleToggleFeatured(review.review_id, review.featured)}
                      title={review.featured ? 'Remove from featured' : 'Add to featured'}
                    >
                      <Award className={`h-4 w-4 ${review.featured ? 'text-purple-600' : 'text-gray-400'}`} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleToggleApproval(review.review_id, review.approved !== false)}
                      title={review.approved !== false ? 'Hide review' : 'Approve review'}
                    >
                      {review.approved !== false ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-green-600" />
                      )}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditModal(review)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(review.review_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingReview ? 'Edit Review' : 'Add Review'}</DialogTitle>
            <DialogDescription>
              {editingReview ? 'Update the review details' : 'Add a new customer review'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label>Location</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Cape Town"
                />
              </div>
            </div>

            <div>
              <Label>Rating *</Label>
              <div className="mt-2">
                <StarRating 
                  rating={formData.rating} 
                  size="large" 
                  interactive 
                  onChange={(rating) => setFormData(prev => ({ ...prev, rating }))}
                />
              </div>
            </div>

            <div>
              <Label>Service</Label>
              <Select 
                value={formData.service} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, service: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="delivery">Delivery</SelectItem>
                  <SelectItem value="mini-move">Mini-Move</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Review Text *</Label>
              <Textarea
                value={formData.text}
                onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
                placeholder="Customer's review..."
                rows={4}
              />
            </div>

            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={formData.verified}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, verified: checked }))}
                />
                <Label>Verified</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={formData.approved}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, approved: checked }))}
                />
                <Label>Approved</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={formData.featured}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, featured: checked }))}
                />
                <Label>Featured</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingReview ? 'Update Review' : 'Add Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReviewsPanel;
